import { useRef, useState } from 'react';

import {
    OnboardingDocumentReadProgress,
    OnboardingDocumentReadState,
    OnboardingFileView,
    OnboardingStatus,
} from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/utils/cn';
import { useFeeSchedules } from 'views/billing/fee-schedules/fee-schedule-queries';
import { DateTimeDisplay } from 'widgets';

import { AutosaveNote, CardFooter, needsAttention, SectionHeading, SelectField } from './fields';
import { FilePreviewDialog } from './FilePreviewDialog';
import {
    documentsReadable,
    feeScheduleEditable,
    fileReadLabel,
    isReadingDocuments,
    needsReading,
} from './onboarding-model';
import {
    onboardingFilesArchiveUrl,
    onboardingFileUrl,
    useDocumentReadProgress,
    useOnboardingFiles,
    useOnboardingStatus,
    useSetOnboardingFeeSchedule,
    useStartDocumentReading,
    useUploadOnboardingFile,
} from './onboarding-tab-queries';
import { useAutosave } from './useAutosave';
import { OnboardingHousehold } from './useOnboardingHousehold';

export const AgreementsView = ({
    householdGUID,
    household,
}: {
    householdGUID: string;
    household: OnboardingHousehold;
}) => (
    <div className="flex flex-col gap-11">
        <AgreementSection householdGUID={householdGUID} household={household} />
        <DocumentsSection householdGUID={householdGUID} household={household} />
    </div>
);

/**
 * The Claro Client Agreement: the household's fee schedule, picked from its tenant's while the onboarding is collected or
 * reviewed. The billing group is created from it when the accounts are opened.
 */
const AgreementSection = ({ householdGUID, household }: { householdGUID: string; household: OnboardingHousehold }) => {
    const [open, setOpen] = useState(true);
    const { data: status, isLoading, isError } = useOnboardingStatus(householdGUID);
    return (
        <section className="flex flex-col gap-3.5">
            <SectionHeading open={open} onToggle={() => setOpen(!open)}>
                Claro Client Agreement
            </SectionHeading>
            {open && (
                <div className="border-border rounded-lg border bg-white px-5 pt-5">
                    {isLoading ? (
                        <p className="text-cocoa-500 pb-5 text-sm">Loading…</p>
                    ) : isError || !status ? (
                        <p role="alert" className="pb-5">
                            Could not load the onboarding.
                        </p>
                    ) : (
                        <FeeScheduleForm
                            householdGUID={householdGUID}
                            tenantGUID={household.tenantGUID}
                            status={status}
                        />
                    )}
                </div>
            )}
        </section>
    );
};

const FeeScheduleForm = ({
    householdGUID,
    tenantGUID,
    status,
}: {
    householdGUID: string;
    tenantGUID: string | undefined;
    status: OnboardingStatus;
}) => {
    const { data: feeSchedules = [], isLoading } = useFeeSchedules(tenantGUID ? [tenantGUID] : []);
    const save = useSetOnboardingFeeSchedule(householdGUID);
    const {
        values: feeScheduleGUID,
        setValues: setFeeScheduleGUID,
        autosave,
    } = useAutosave({
        stored: status.feeScheduleGUID ?? '',
        check: (picked) => (picked ? undefined : 'Select a fee schedule'),
        save: (picked) => save.mutateAsync(picked),
    });
    const editable = feeScheduleEditable(status.stage);
    return (
        <div {...autosave.bodyProps} className={cn('flex flex-col gap-4', !editable && 'pb-5')}>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,220px),1fr))] items-start gap-x-5 gap-y-4">
                <SelectField
                    label="Fee schedule"
                    value={feeScheduleGUID}
                    onChange={setFeeScheduleGUID}
                    options={feeSchedules.map((schedule) => ({
                        value: schedule.feeScheduleGUID,
                        label: schedule.feeScheduleName,
                    }))}
                    placeholder={isLoading ? 'Loading…' : 'Select…'}
                    disabled={!editable}
                />
            </div>
            {editable && (
                <CardFooter floating={needsAttention(autosave)}>
                    <AutosaveNote autosave={autosave} />
                </CardFooter>
            )}
        </div>
    );
};

/** How far the reading of the documents is, or how many it left unread. */
const ReadingStatus = ({ progress, unread }: { progress: OnboardingDocumentReadProgress; unread: number }) => {
    const documents = (count: number) => `${count} ${count === 1 ? 'document' : 'documents'}`;
    let text: string;
    if (progress.state === OnboardingDocumentReadState.READING) {
        text = `Reading the documents · ${progress.done} of ${progress.total} done`;
    } else if (progress.state === OnboardingDocumentReadState.BUILDING) {
        text = 'Adding what was read to the household…';
    } else if (unread > 0) {
        text = `${documents(unread)} not read or could not be read`;
    } else {
        text = 'Every document is read';
    }
    return (
        <div className="border-border flex items-center gap-4 border-b px-5 py-3 text-sm">
            <span>{text}</span>
            {progress.state === OnboardingDocumentReadState.READING && (
                <Progress
                    value={progress.total > 0 ? (100 * progress.done) / progress.total : 0}
                    className="h-1.5 max-w-60"
                />
            )}
        </div>
    );
};

const DocumentsSection = ({ householdGUID, household }: { householdGUID: string; household: OnboardingHousehold }) => {
    const [open, setOpen] = useState(true);
    const { data: files = [], isLoading } = useOnboardingFiles(householdGUID);
    const upload = useUploadOnboardingFile(householdGUID);
    const input = useRef<HTMLInputElement>(null);
    const [previewFile, setPreviewFile] = useState<OnboardingFileView | null>(null);
    const { data: status } = useOnboardingStatus(householdGUID);
    const { data: progress } = useDocumentReadProgress(householdGUID);
    const startReading = useStartDocumentReading(householdGUID);
    const reading = isReadingDocuments(progress?.state);
    const unread = files.filter(needsReading).length;

    return (
        <section className="flex flex-col gap-3.5">
            <SectionHeading
                open={open}
                onToggle={() => setOpen(!open)}
                action={
                    <>
                        <input
                            ref={input}
                            type="file"
                            accept="application/pdf,image/jpeg,image/png"
                            className="hidden"
                            onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (file) upload.mutate(file);
                                event.target.value = '';
                            }}
                        />
                        {documentsReadable(status?.stage) && files.length > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={reading || startReading.isPending}
                                title={
                                    reading
                                        ? 'The documents are being read'
                                        : 'Read every document again; what the client changed is kept'
                                }
                                onClick={() => startReading.mutate()}
                            >
                                Read all documents
                            </Button>
                        )}
                        {files.length > 0 && (
                            <Button asChild variant="outline" size="sm">
                                <a href={onboardingFilesArchiveUrl(householdGUID)} download>
                                    Download all
                                </a>
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={upload.isPending}
                            onClick={() => input.current?.click()}
                        >
                            {upload.isPending ? 'Uploading…' : 'Upload'}
                        </Button>
                    </>
                }
            >
                Documents
            </SectionHeading>
            {open && (
                <div className="border-border overflow-hidden rounded-lg border bg-white">
                    {progress && files.length > 0 && <ReadingStatus progress={progress} unread={unread} />}
                    <div className="border-border text-cocoa-400 grid grid-cols-[minmax(0,2.4fr)_minmax(0,2fr)_minmax(0,1.2fr)_140px_130px] gap-3 border-b px-5 py-2.5 text-xs font-medium tracking-[0.08em] uppercase">
                        <span>File</span>
                        <span>Type</span>
                        <span>Uploaded by</span>
                        <span>Date</span>
                        <span />
                    </div>
                    {!isLoading && files.length === 0 && (
                        <div className="text-cocoa-400 px-5 py-4 text-sm">No documents uploaded</div>
                    )}
                    {files.map((file) => (
                        <div
                            key={file.onboardingFileGUID}
                            className="border-cocoa-100 grid grid-cols-[minmax(0,2.4fr)_minmax(0,2fr)_minmax(0,1.2fr)_140px_130px] items-center gap-3 border-b px-5 py-2.5 text-sm last:border-b-0"
                        >
                            <span className="break-all">{file.fileName}</span>
                            <span className="text-cocoa-600">{fileReadLabel(file)}</span>
                            {/* Staff uploads are by someone outside the household, whose name is not loaded here */}
                            <span>{household.nameOf(file.uploadedBy) || 'Claro'}</span>
                            <span className="text-cocoa-600">
                                <DateTimeDisplay dateString={file.createdAt} />
                            </span>
                            <span className="flex gap-3">
                                <Button variant="link" className="h-auto p-0" onClick={() => setPreviewFile(file)}>
                                    Open
                                </Button>
                                <Button asChild variant="link" className="h-auto p-0">
                                    <a href={onboardingFileUrl(householdGUID, file)} download>
                                        Download
                                    </a>
                                </Button>
                            </span>
                        </div>
                    ))}
                </div>
            )}
            {previewFile && (
                <FilePreviewDialog
                    householdGUID={householdGUID}
                    file={previewFile}
                    onClose={() => setPreviewFile(null)}
                />
            )}
        </section>
    );
};
